import { useCallback, useMemo } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { TableBody as AriaTableBody } from 'react-aria-components'
import { Table, TableCard } from '@/components/application/table/table'
import { PaginationTable } from '@/components/application/pagination/pagination'
import { Button } from '@/components/base/buttons'
import { Tooltip, TooltipTrigger } from '@/components/ui/Tooltip'
import { PlayIcon, RefreshCcw01Icon } from '@untitledui-icons/react/line'
import { useSimBatches, useSimScenarios } from '@/hooks/api/useSimulations'
import { useAssistants } from '@/hooks/api/useAssistants'
import { useAIDeployments } from '@/hooks/api/useAIDeployments'
import { StatusBadge } from './StatusBadge'
import { PassRateBar } from './PassRateBar'
import type { SimBatch } from '@/types/simulation'

interface SimBatchesTabProps {
  onRunSimulation: () => void
  onRerunBatch: (batch: SimBatch) => void
}

/**
 * Batches tab - displays list of simulation batches with progress
 */
export function SimBatchesTab({ onRunSimulation, onRerunBatch }: SimBatchesTabProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()

  // Pagination state from URL
  const page = parseInt(searchParams.get('page') || '1', 10)
  const perPage = parseInt(searchParams.get('perPage') || '25', 10)

  // Fetch batches with polling for active ones
  const { data: batchesData, isLoading, isFetching } = useSimBatches({ page, perPage })

  const batches = batchesData?.data ?? []
  const totalCount = batchesData?.total ?? 0
  const totalPages = Math.ceil(totalCount / perPage)

  // Fetch assistants, scenarios, and AI deployments for name lookups
  const { data: assistants = [] } = useAssistants()
  const { data: scenarios = [] } = useSimScenarios()
  const { data: aiDeployments = [] } = useAIDeployments()

  // Create lookup maps for efficient name resolution
  const assistantMap = useMemo(() => {
    return new Map(assistants.map((a) => [a.id, a.name]))
  }, [assistants])

  const scenarioMap = useMemo(() => {
    return new Map(scenarios.map((s) => [s.id, s.name]))
  }, [scenarios])

  const aiDeploymentMap = useMemo(() => {
    return new Map(aiDeployments.map((d) => [d.id, d.friendly_name || `${d.vendor}/${d.model}`]))
  }, [aiDeployments])

  // Format assistant display: name if 1, "X assistants" if multiple
  const formatAssistants = useCallback(
    (assistantIds: number[]) => {
      if (assistantIds.length === 0) return '-'
      if (assistantIds.length === 1) {
        return assistantMap.get(assistantIds[0]) || `#${assistantIds[0]}`
      }
      return t('pages.simulations.batches.columns.assistants_count', {
        count: assistantIds.length,
      })
    },
    [assistantMap, t]
  )

  // Format scenario display: name if 1, "X scenarios" if multiple
  const formatScenarios = useCallback(
    (scenarioIds: number[]) => {
      if (scenarioIds.length === 0) return '-'
      if (scenarioIds.length === 1) {
        return scenarioMap.get(scenarioIds[0]) || `#${scenarioIds[0]}`
      }
      return t('pages.simulations.batches.columns.scenarios_count', {
        count: scenarioIds.length,
      })
    },
    [scenarioMap, t]
  )

  // Check if any batches are still in progress
  const hasActiveBatches = batches.some(
    (batch) => batch.status === 'pending' || batch.status === 'running'
  )

  // Handle row click - navigate to batch detail
  const handleRowClick = useCallback(
    (batch: SimBatch) => {
      navigate(`/simulations/batches/${batch.id}`)
    },
    [navigate]
  )

  // Handle page change
  const handlePageChange = useCallback(
    (newPage: number) => {
      const newParams = new URLSearchParams(searchParams)
      newParams.set('page', String(newPage))
      setSearchParams(newParams)
    },
    [searchParams, setSearchParams]
  )

  // Handle page size change
  const handlePageSizeChange = useCallback(
    (newPerPage: number) => {
      const newParams = new URLSearchParams(searchParams)
      newParams.set('perPage', String(newPerPage))
      newParams.set('page', '1') // Reset to first page
      setSearchParams(newParams)
    },
    [searchParams, setSearchParams]
  )

  // Format date for display
  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-'
    const date = new Date(dateStr)
    return new Intl.DateTimeFormat('en-GB', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date)
  }

  // Calculate pass rate for a batch
  const getPassRate = (batch: SimBatch) => {
    if (batch.completed_runs === 0) return 0
    return batch.passed_runs / batch.completed_runs
  }

  // Run Simulation button component for reuse
  const runButton = (
    <Button onClick={onRunSimulation} icon={PlayIcon} size="sm">
      {t('pages.simulations.run_simulation')}
    </Button>
  )

  // Loading state
  if (isLoading) {
    return (
      <TableCard.Root size="sm" className="flex h-full flex-col">
        <TableCard.Header
          title={t('pages.simulations.batches.title')}
          contentTrailing={runButton}
        />
        <div className="flex flex-1 items-center justify-center py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-500 border-t-transparent" />
        </div>
      </TableCard.Root>
    )
  }

  // Empty state
  if (batches.length === 0) {
    return (
      <TableCard.Root size="sm" className="flex h-full flex-col">
        <TableCard.Header
          title={t('pages.simulations.batches.title')}
          contentTrailing={runButton}
        />
        <div className="flex flex-1 flex-col items-center justify-center gap-4 py-12">
          <p className="text-gray-500">{t('pages.simulations.batches.empty')}</p>
          <Button onClick={onRunSimulation} icon={PlayIcon}>
            {t('pages.simulations.run_simulation')}
          </Button>
        </div>
      </TableCard.Root>
    )
  }

  return (
    <div className="flex h-full flex-col">
      <TableCard.Root size="sm" className="relative flex flex-1 flex-col">
        <TableCard.Header
          title={t('pages.simulations.batches.title')}
          contentTrailing={
            <div className="flex items-center gap-3">
              {/* Live indicator when batches are in progress */}
              {hasActiveBatches && (
                <div className="flex items-center gap-1.5 text-xs text-gray-500">
                  <div className="h-2 w-2 animate-pulse rounded-full bg-brand-500" />
                  <span>{t('pages.simulations.batches.live')}</span>
                </div>
              )}
              {/* Fetching indicator */}
              {isFetching && !isLoading && (
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-brand-500 border-t-transparent" />
              )}
              {runButton}
            </div>
          }
        />

        <div className="flex-1 overflow-auto">
        <Table aria-label={t('pages.simulations.batches.title')}>
          <Table.Header>
            <Table.Head id="id" label={t('pages.simulations.batches.columns.id')} isRowHeader />
            <Table.Head id="assistants" label={t('pages.simulations.batches.columns.assistants')} />
            <Table.Head id="scenarios" label={t('pages.simulations.batches.columns.scenarios')} />
            <Table.Head id="status" label={t('pages.simulations.batches.columns.status')} />
            <Table.Head id="progress" label={t('pages.simulations.batches.columns.progress')} />
            <Table.Head id="pass_rate" label={t('pages.simulations.batches.columns.pass_rate')} />
            <Table.Head id="ai_model" label={t('pages.simulations.batches.columns.ai_model')} />
            <Table.Head id="created_at" label={t('pages.simulations.batches.columns.created_at')} />
            <Table.Head id="actions" label="" className="w-12" />
          </Table.Header>

          <AriaTableBody>
            {batches.map((batch) => (
              <Table.Row
                key={batch.id}
                className="cursor-pointer transition-colors hover:bg-gray-50 dark:hover:bg-gray-800/50"
                onAction={() => handleRowClick(batch)}
              >
                <Table.Cell className="font-medium">#{batch.id}</Table.Cell>
                <Table.Cell className="max-w-[200px] truncate" title={formatAssistants(batch.assistant_ids)}>
                  {formatAssistants(batch.assistant_ids)}
                </Table.Cell>
                <Table.Cell className="max-w-[200px] truncate" title={formatScenarios(batch.scenario_ids)}>
                  {formatScenarios(batch.scenario_ids)}
                </Table.Cell>
                <Table.Cell>
                  <StatusBadge status={batch.status} />
                </Table.Cell>
                <Table.Cell>
                  <ProgressIndicator batch={batch} />
                </Table.Cell>
                <Table.Cell className="w-36">
                  {batch.completed_runs > 0 ? (
                    <PassRateBar passRate={getPassRate(batch)} size="sm" />
                  ) : (
                    <span className="text-sm text-gray-400">-</span>
                  )}
                </Table.Cell>
                <Table.Cell className="text-gray-500 text-xs">
                  <div className="space-y-0.5">
                    {batch.ai_deployment_id_override && (
                      <div title={t('pages.simulations.batches.model_assistant')}>
                        <span className="font-medium">A:</span> {aiDeploymentMap.get(batch.ai_deployment_id_override) || `#${batch.ai_deployment_id_override}`}
                      </div>
                    )}
                    {batch.simulator_ai_deployment_ids?.length > 0 && (
                      <div title={t('pages.simulations.batches.model_simulator')}>
                        <span className="font-medium">S:</span> {batch.simulator_ai_deployment_ids.length === 1
                          ? aiDeploymentMap.get(batch.simulator_ai_deployment_ids[0]) || `#${batch.simulator_ai_deployment_ids[0]}`
                          : t('resources.simulations.models_count', { count: batch.simulator_ai_deployment_ids.length })}
                      </div>
                    )}
                    {batch.judge_ai_deployment_ids?.length > 0 && (
                      <div title={t('pages.simulations.batches.model_judge')}>
                        <span className="font-medium">J:</span> {batch.judge_ai_deployment_ids.length === 1
                          ? aiDeploymentMap.get(batch.judge_ai_deployment_ids[0]) || `#${batch.judge_ai_deployment_ids[0]}`
                          : t('resources.simulations.models_count', { count: batch.judge_ai_deployment_ids.length })}
                      </div>
                    )}
                    {!batch.ai_deployment_id_override && 
                     (!batch.simulator_ai_deployment_ids || batch.simulator_ai_deployment_ids.length === 0) && 
                     (!batch.judge_ai_deployment_ids || batch.judge_ai_deployment_ids.length === 0) && (
                      <span>{t('pages.simulations.batches.columns.ai_model_default')}</span>
                    )}
                  </div>
                </Table.Cell>
                <Table.Cell className="text-gray-500">
                  {formatDate(batch.created_at)}
                </Table.Cell>
                <Table.Cell>
                  <TooltipTrigger delay={300}>
                    <Button
                      iconLeading={RefreshCcw01Icon}
                      size="sm"
                      color="tertiary"
                      aria-label={t('pages.simulations.batches.run_again')}
                      onPress={() => onRerunBatch(batch)}
                    />
                    <Tooltip>{t('pages.simulations.batches.run_again_tooltip')}</Tooltip>
                  </TooltipTrigger>
                </Table.Cell>
              </Table.Row>
            ))}
          </AriaTableBody>
        </Table>
      </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <PaginationTable
            page={page}
            total={totalPages}
            perPage={perPage}
            onPageChange={handlePageChange}
            onPerPageChange={handlePageSizeChange}
          />
        )}
      </TableCard.Root>
    </div>
  )
}

/**
 * Progress indicator showing completed/total runs with mini progress bar
 */
function ProgressIndicator({ batch }: { batch: SimBatch }) {
  const progress = batch.total_runs > 0 ? batch.completed_runs / batch.total_runs : 0
  const isComplete = batch.completed_runs === batch.total_runs
  const isRunning = batch.status === 'running' || batch.status === 'pending'

  return (
    <div className="flex items-center gap-2">
      <div className="flex min-w-[60px] items-center gap-1 text-sm">
        <span className={isComplete ? 'text-success-600 dark:text-success-400' : ''}>
          {batch.completed_runs}
        </span>
        <span className="text-gray-400">/</span>
        <span className="text-gray-500">{batch.total_runs}</span>
      </div>

      {/* Mini progress bar */}
      <div className="h-1.5 w-20 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
        <div
          className={`h-full transition-all duration-300 ${
            isComplete
              ? 'bg-success-500'
              : isRunning
                ? 'bg-brand-500'
                : 'bg-gray-400'
          }`}
          style={{ width: `${progress * 100}%` }}
        />
      </div>
    </div>
  )
}

export default SimBatchesTab
